import EventManager from '../../eventManager/EventManager'
import { Device } from '../../helpers'
import { doOnce } from '../../helpers/doOnce'
import { queryParamHas } from '../../helpers/getQueryParam'
import WebModel from '../../models/WebModel'
import { setupAncestorsForSticky } from '../../stickySlot/StickySlot'
import { Slot } from '../Slot'

const maxAdHeight = 480
const adReportHeight = 24
const topBuffer = 10
export const railFrameHeight = maxAdHeight + adReportHeight + topBuffer
const maxAdWidth = 320
const enabledOnDesktop = false

const additionalSizes: AdSize[] = [[320, 480]]

export const hasRail = (target: Constructor<Slot>): any =>
  class extends target {
    constructor(...args) {
      super(...args)

      if (!railIsEnabled(this.model)) {
        return
      }

      this.setupSlotAndModel()
    }

    sizeContainersLogic() {
      super.sizeContainersLogic()
      const { height, width } = this

      if (!railIsEnabled(this.model)) {
        return
      }
      if (height <= 1 || width <= 1) {
        this.sticky.style.width = 'fit-content'
        this.sticky.style.height = 'fit-content'
      } else {
        this.sticky.style.width = `${width}px`
        this.sticky.style.height = `${height + adReportHeight}px`
      }
    }

    private _frame: HTMLElement
    get frame() {
      this._frame = this._frame || createElement('div', { class: 'rail-frame' })

      return this._frame
    }

    private _slide: HTMLElement
    get slide() {
      this._slide = this._slide || createElement('div', { class: 'rail-slide' })

      return this._slide
    }

    private _sticky: HTMLElement
    get sticky() {
      this._sticky =
        this._sticky || createElement('div', { class: 'rail-sticky' })

      return this._sticky
    }

    private createRailFrame() {
      const railFrame = this.frame
      const railSlide = this.slide
      const railSticky = this.sticky
      railSlide.appendChild(railSticky)
      railFrame.appendChild(railSlide)

      return railFrame
    }

    private wrapElement(el: HTMLElement) {
      if (!el.parentElement) {
        return
      }
      const railFrame = this.createRailFrame()
      el.parentElement.insertBefore(railFrame, el)
      if (!railFrame.firstChild || !railFrame.firstChild.firstChild) {
        return
      }
      railFrame.firstChild.firstChild.appendChild(el)
    }

    private addAdditionalSizes() {
      if (Device.isMobileOrTablet) {
        additionalSizes.forEach((currentSize) => {
          const hasSize = this._sizes.some(
            (adSize) =>
              typeof adSize === 'object' &&
              adSize[0] === currentSize[0] &&
              adSize[1] === currentSize[1]
          )
          if (!hasSize) {
            this._sizes.push(currentSize)
          }
        })
      }
    }

    private setupSlotAndModel() {
      // using the rail comes with additional larger ads per the provided height
      this.addAdditionalSizes()

      // setup slot config with rail properties
      this.onRail = true
      this.restrictRefreshSize = false
      // when used with adbox, configure it to fit the rail
      if (this.adBoxConfig) {
        this.adBoxConfig.heightToContain = railFrameHeight
        this.adBoxConfig.width = '100%'
      }

      // after wrapper has rendered, wrap the slot in DOM that makes it rail
      EventManager.on(EventManager.events.slotWrapperRenderEnded, (slot) => {
        if (slot !== this) {
          return
        }
        insertStyles(getStyles(this.model))
        this.wrapElement(this.wrapper || document.createElement('div'))
        setupAncestorsForSticky(this.sticky, queryParamHas('test', 'rail'))
      })
    }
  }

export function railIsEnabled(model: WebModel): boolean {
  return !!model.mobile_inview && (Device.isMobileOrTablet || enabledOnDesktop)
}

const insertStyles = doOnce((styles: string) => {
  const el = createElement('style')
  el.innerText = styles
  document.head.appendChild(el)
})

function createElement(elType, attributes?): HTMLElement {
  const dummyDiv = document.createElement('div')
  dummyDiv.innerHTML = `<${elType}></${elType}>`
  const el = dummyDiv.firstChild as HTMLElement
  Object.keys(attributes || {}).map((k) => el.setAttribute(k, attributes[k]))

  return el
}

const contentDots = '\\00B7\\00B7\\00B7'

function getStyles({
  ad_box_bg_color,
  sidebar_btf_sticky_offset = 0
}: WebModel) {
  const offset = (sidebar_btf_sticky_offset || 0) + topBuffer
  return `
.rail-frame {
  position:relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  height:${railFrameHeight}px;
  margin: 0px 0px;
  clear: both;
  background-color: ${ad_box_bg_color};
}

.rail-slide {
  display: flex;
  justify-content: center;
  width: 100%; 
  height: 100%;
  margin-top: ${topBuffer}px;
}

.rail-sticky {
  box-sizing: border-box;
  position: -webkit-sticky;
  position: sticky;
  display: flex;
  flex-direction: column;
  align-content: center;
  top: ${offset}px;
  width: ${maxAdWidth}px;
  height: ${maxAdHeight + adReportHeight}px;
  min-height: 74px;
}

.rail-sticky:before {
  content: "${contentDots}";
  position: absolute;
  display: flex!important;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  text-align: center;
  padding-bottom: 15px;
  color: #C4C4C4;
  font-size: 62px;
  letter-spacing: 2px;
  z-index: -1;
  animation: fadeOut 1s linear 0s infinite;
  animation-direction: alternate;
}

.rail-sticky>.adunitwrapper {
  height:initial!important;
  overflow: hidden!important;
  margin: 0;
}

.content-container {
  overflow-x: initial!important;
}

${getGrowStyleHack(offset)}
${getCreateArrivalStyleHack()}
`
}

// NOTE: we would not want to have these long term
function getGrowStyleHack(offset: number): string {
  return `body.grow-me-scroll-carousel-active .rail-sticky {
    top: ${offset + 60}px;
  }`
}

const arrivalSlotBuffer = 55
function getCreateArrivalStyleHack() {
  return `.mv-pre-create-target[style*=height] {
    height: ${railFrameHeight + arrivalSlotBuffer}px !important;
    width: 100% !important;
  }`
}
